import { HttpClient } from '@angular/common/http';
import { ApplicationRef, Injectable, inject } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { resolvePreferredLocale } from '../i18n/locale-utils';
import { environment } from 'src/environments/environment';
import {
    ExperienceBundleParams,
    ExperienceBundleResponse,
    ExperienceManifestResponse,
    flattenTranslations,
} from './experience.models';
import {
    resolveExperienceRubric,
    resolveExperienceTenantScope,
} from './experience-params.util';
import { resolveExperienceI18nKey } from './experience-i18n-key-aliases';
import { CustomValidators } from '../validators/custom-validators';

@Injectable({
    providedIn: 'root'
})
export class ExperienceI18nService {
    private readonly http = inject(HttpClient);
    private readonly appRef = inject(ApplicationRef);
    private readonly flatTranslations: Record<string, string> = {};
    private currentLocale = 'es-AR';
    private readonly localeChangedSubject = new Subject<string>();

    readonly localeChanged$ = this.localeChangedSubject.asObservable();

    constructor() {
        CustomValidators.translate = (key, fallback) => this.translate(key, undefined, fallback);
    }

    async load(): Promise<void> {
        if (!environment.experienceI18nEnabled) {
            return;
        }

        const params = this.buildParams();
        this.currentLocale = params.locale;
        await this.fetchAndCache(params);
    }

    async reload(): Promise<void> {
        if (!environment.experienceI18nEnabled) {
            return;
        }

        const params = this.buildParams();
        this.clearCacheForParams(params);
        await this.fetchAndCache(params);
        this.currentLocale = params.locale;
        document.documentElement.lang = params.locale;
        // Always notify: rubric/tenant can change while locale stays the same (e.g. login DEFAULT → SPORT).
        this.localeChangedSubject.next(params.locale);
        this.appRef.tick();
    }

    getLocale(): string {
        return this.currentLocale;
    }

    translate(key: string, params?: Record<string, string | number>, fallback?: string): string {
        const resolvedKey = resolveExperienceI18nKey(key);
        const template = this.flatTranslations[resolvedKey] ?? resolvedKey;
        const resolved = !params
            ? template
            : Object.entries(params).reduce(
                (text, [name, value]) =>
                    text.replace(new RegExp(`\\{\\{${name}\\}\\}`, 'g'), String(value)),
                template,
            );
        if ((resolved === resolvedKey || resolved === key) && fallback) {
            return fallback;
        }
        return resolved;
    }

    private async fetchAndCache(params: ExperienceBundleParams): Promise<void> {
        const versionKey = this.versionStorageKey(params);
        const cacheKey = this.translationStorageKey(params);

        try {
            const manifest = await firstValueFrom(
                this.http.get<ExperienceManifestResponse>(
                    `${environment.experienceApiBaseUrl}/i18n/manifest`,
                    { params: this.toHttpParams(params) }
                )
            );

            const cachedVersion = localStorage.getItem(versionKey);
            const cachedTranslations = localStorage.getItem(cacheKey);
            if (
                cachedVersion === manifest.contentVersion &&
                cachedTranslations
            ) {
                this.applyTranslations(JSON.parse(cachedTranslations) as Record<string, string>);
                return;
            }

            const bundle = await firstValueFrom(
                this.http.get<ExperienceBundleResponse>(
                    `${environment.experienceApiBaseUrl}/i18n/bundle`,
                    { params: this.toHttpParams(params) }
                )
            );

            const flat = flattenTranslations(bundle.translations);
            this.applyTranslations(flat);
            localStorage.setItem(versionKey, bundle.contentVersion);
            localStorage.setItem(cacheKey, JSON.stringify(flat));
        } catch (error) {
            console.warn('[ExperienceI18n] Remote load failed', error);
            const cachedTranslations = localStorage.getItem(cacheKey);
            if (cachedTranslations) {
                this.applyTranslations(JSON.parse(cachedTranslations) as Record<string, string>);
            }
        }
    }

    private applyTranslations(flat: Record<string, string>): void {
        Object.keys(this.flatTranslations).forEach(
            (key) => delete this.flatTranslations[key]
        );
        Object.assign(this.flatTranslations, flat);
    }

    private clearCacheForParams(params: ExperienceBundleParams): void {
        localStorage.removeItem(this.versionStorageKey(params));
        localStorage.removeItem(this.translationStorageKey(params));
    }

    private buildParams(): ExperienceBundleParams {
        const user = this.readUserProfile();
        const tenant = this.readTenantProfile();
        return {
            appId: environment.experienceAppId,
            appVersion: environment.experienceAppVersion ?? '1.0.0',
            locale: resolvePreferredLocale(user?.preferredLocale),
            rubric: resolveExperienceRubric(tenant?.experienceRubric, user?.rubric),
            tenantScope: resolveExperienceTenantScope(tenant),
        };
    }

    private readUserProfile(): { preferredLocale?: string; rubric?: string } | null {
        try {
            const raw = localStorage.getItem('user');
            const user = raw ? JSON.parse(raw) : null;
            return {
                preferredLocale: resolvePreferredLocale(user?.preferredLocale),
                rubric: user?.rubric?.trim() || 'DEFAULT',
            };
        } catch {
            return null;
        }
    }

    private readTenantProfile(): { id?: number; code?: string; experienceRubric?: string } | null {
        try {
            const raw = localStorage.getItem('tenant');
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    private toHttpParams(params: ExperienceBundleParams): Record<string, string> {
        return {
            appId: params.appId,
            appVersion: params.appVersion,
            locale: params.locale,
            rubric: params.rubric,
            tenantScope: params.tenantScope,
        };
    }

    private versionStorageKey(params: ExperienceBundleParams): string {
        return `experience_i18n_version_${params.appId}_${params.locale}_${params.rubric}_${params.tenantScope}`;
    }

    private translationStorageKey(params: ExperienceBundleParams): string {
        return `experience_i18n_cache_${params.appId}_${params.locale}_${params.rubric}_${params.tenantScope}`;
    }
}

export function initExperienceI18n(service: ExperienceI18nService): () => Promise<void> {
    return () => service.load();
}
